import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import type { PeripheralBlock, TerminalPeripheralConfigOverride } from '@reform-lite/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/auth/auth-store';
import {
  PERIPHERAL_BLOCK_LABELS,
  AUTHORABLE_PERIPHERAL_BLOCKS,
  downloadSoftFiscalConfig,
  useTerminalPeripheralConfig,
  useUpdateTerminalPeripheralConfig,
  type PeripheralBlockOrigin,
} from '@/hooks/use-terminals';
import {
  BLOCK_DEFAULTS,
  PeripheralBlockFields,
  summarisePeripheralBlock,
} from './peripheral-fields';
import { useT } from '@/i18n/use-t';

/**
 * RFL-241 — a terminal's hardware, against what its group provides.
 *
 * ------------------------------------------------------------------
 * WHY THE THREE STATES ARE SPELLED OUT
 * ------------------------------------------------------------------
 * The same reasoning as the config screen next door, applied to hardware,
 * where getting it wrong is more visible. A block on a terminal is one of:
 *
 *   inherited  — absent from the override; the group's value is delivered
 *   overridden — set here; this value is delivered instead
 *   cleared    — an explicit `null`; NOTHING is delivered for this block and
 *                the till falls back to its own build define
 *
 * "Cleared" is not "inherited with nothing" and not "overridden with blanks".
 * It is the only way to say "this store's profile sets a scanner port, and
 * this one lane has a keyboard-wedge scanner instead" — and if the screen
 * collapsed it into either neighbour, that instruction would be unexpressible
 * from Back Office while staying perfectly expressible through the API.
 *
 * The merged result and the origin of every block are read from the server,
 * never recomputed here. A screen that disagreed with the sync feed about
 * whether a port is inherited would be believed over the feed.
 */

const ORIGIN_LABEL: Record<PeripheralBlockOrigin, string> = {
  group: 'Inherited from group',
  terminal: 'Set on this terminal',
  cleared: 'Cleared — till uses its own default',
  unset: 'Not authored anywhere',
};

const ORIGIN_TONE: Record<PeripheralBlockOrigin, 'default' | 'outline'> = {
  group: 'outline',
  terminal: 'default',
  cleared: 'outline',
  unset: 'outline',
};

type BlockState = 'inherited' | 'overridden' | 'cleared';

function stateOf(override: TerminalPeripheralConfigOverride, block: PeripheralBlock): BlockState {
  if (!(block in override)) return 'inherited';
  return override[block] === null ? 'cleared' : 'overridden';
}

export function TerminalPeripheralConfigPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const view = useTerminalPeripheralConfig(id);
  const update = useUpdateTerminalPeripheralConfig();
  const canWrite = useHasPermission('terminal-management.write');
  const [draft, setDraft] = useState<TerminalPeripheralConfigOverride | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (view.isLoading)
    return (
      <p className="text-sm text-muted-foreground">{t('terminal.hardware.loading', 'Loading…')}</p>
    );
  if (view.data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('terminal.hardware.notFound', 'Terminal not found.')}
      </p>
    );
  }

  const { profile, profileConfig, effective, origins } = view.data;
  const override = draft ?? view.data.override;
  const dirty = draft !== null;
  const blocks = AUTHORABLE_PERIPHERAL_BLOCKS;

  const setState = (block: PeripheralBlock, next: BlockState) => {
    const copy: TerminalPeripheralConfigOverride = { ...override };
    if (next === 'inherited') delete copy[block];
    else if (next === 'cleared') copy[block] = null;
    else {
      // Seed from what the till has today, so overriding one field does not
      // mean retyping the rest.
      const seed = (effective[block] ?? BLOCK_DEFAULTS[block]) as never;
      copy[block] = seed;
    }
    setDraft(copy);
    setError(null);
  };

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('terminal.hardware.title', 'Terminal hardware')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {profile === null ? (
              <>
                {t(
                  'terminal.hardware.noProfile',
                  'This terminal is bound to no profile, so there is nothing to inherit — every block below is either set here or absent.',
                )}
              </>
            ) : (
              <>
                {t('terminal.hardware.inheritingFrom', 'Inheriting from')}{' '}
                <Link className="underline" to="/terminal-profiles">
                  {profile.name}
                </Link>
                {t(
                  'terminal.hardware.inheritanceHelp',
                  ". A value set here wins over the group's. Changes reach the till on its next sync and take effect when it restarts.",
                )}
              </>
            )}
          </p>
        </div>
        <Link className="text-sm underline" to={`/terminals/${id ?? ''}`}>
          {t('terminal.hardware.back', 'Back to configuration')}
        </Link>
      </header>

      <div className="space-y-3">
        {blocks.map((block) => {
          const state = stateOf(override, block);
          const value = (override[block] ?? {}) as Record<string, unknown>;
          return (
            <section key={block} className="rounded-lg border p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-semibold">
                  {t(`terminal.hardware.block.${block}`, PERIPHERAL_BLOCK_LABELS[block])}
                </h2>
                <Badge variant={ORIGIN_TONE[origins[block]]}>
                  {t(`terminal.hardware.origin.${origins[block]}`, ORIGIN_LABEL[origins[block]])}
                </Badge>
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                {t('terminal.hardware.group', 'Group')}:{' '}
                {summarisePeripheralBlock(block, profileConfig, t)} ·{' '}
                {t('terminal.hardware.delivered', 'Delivered')}:{' '}
                {summarisePeripheralBlock(block, effective, t)}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {(['inherited', 'overridden', 'cleared'] as const).map((option) => (
                  <Button
                    key={option}
                    size="sm"
                    variant={state === option ? 'default' : 'outline'}
                    disabled={!canWrite}
                    onClick={() => setState(block, option)}
                  >
                    {t(
                      `terminal.hardware.state.${option}`,
                      option === 'inherited'
                        ? 'Inherit'
                        : option === 'overridden'
                          ? 'Set here'
                          : 'Clear',
                    )}
                  </Button>
                ))}
              </div>

              {block === 'softFiscalAndroid' && (
                // A save in flight counts as unsaved until it and its refetch
                // finish (the hook's onSuccess awaits the refetch): Discard
                // during it would otherwise export the previous saved config.
                <SoftFiscalConfigDownload
                  terminalId={id ?? ''}
                  unsaved={dirty || update.isPending}
                />
              )}

              {state === 'overridden' && (
                <div className="mt-3">
                  <PeripheralBlockFields
                    block={block}
                    value={value}
                    disabled={!canWrite}
                    onChange={(next) => {
                      setDraft({ ...override, [block]: next as never });
                      setError(null);
                    }}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      {canWrite ? (
        <div className="flex items-center gap-3">
          <Button
            disabled={!dirty || update.isPending}
            onClick={() => {
              setError(null);
              update.mutate(
                {
                  id: id ?? '',
                  // An override with no blocks is sent as an empty object, not
                  // as null: null clears the terminal's whole statement, and
                  // "I removed my last override" is the same instruction.
                  peripheralConfigOverride: override,
                  expectedPeripheralConfigVersion: view.data.peripheralConfigVersion,
                },
                {
                  onSuccess: () => setDraft(null),
                  onError: (err) => setError(readServerMessage(err, t)),
                },
              );
            }}
          >
            {update.isPending
              ? t('common.saving', 'Saving…')
              : t('terminal.hardware.save', 'Save hardware')}
          </Button>
          {dirty && (
            <Button variant="outline" onClick={() => setDraft(null)}>
              {t('common.discardChanges', 'Discard changes')}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('terminal.hardware.readOnly', 'You have read-only access to terminal hardware.')}
        </p>
      )}
    </div>
  );
}

/**
 * RL-957 — the till's SoftFiscal Android config file, for the APK's import.
 * Built by the server from the SAVED hardware, so it waits for unsaved
 * changes; a refusal shows the server's "what to fix" message.
 */
function SoftFiscalConfigDownload({
  terminalId,
  unsaved,
}: {
  terminalId: string;
  unsaved: boolean;
}) {
  const t = useT('admin_ui');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    if (unsaved || busy) return;
    setBusy(true);
    setError(null);
    try {
      await downloadSoftFiscalConfig(terminalId);
    } catch (err) {
      setError(
        err instanceof Error && err.message !== ''
          ? err.message
          : t(
              'terminal.hardware.softFiscal.downloadFailed',
              'Could not download the SoftFiscal config.',
            ),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 space-y-1">
      <Button size="sm" variant="outline" disabled={unsaved || busy} onClick={() => void run()}>
        {t('terminal.hardware.softFiscal.download', 'Download SoftFiscal config')}
      </Button>
      {unsaved && (
        <p className="text-xs text-muted-foreground">
          {t(
            'terminal.hardware.softFiscal.saveFirst',
            'Save your changes first: the file is built from the saved hardware.',
          )}
        </p>
      )}
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Surface the server's validation message verbatim.
 *
 * The contract schema is strict and its message names the offending field. A
 * generic "invalid configuration" would throw away the only part that tells an
 * operator which value to fix — and this screen exists so the mistake is
 * caught by someone who can still fix it, rather than at the lane.
 */
function readServerMessage(
  err: unknown,
  t: (key: string, bakedEnglish?: string) => string,
): string {
  if (isAxiosError(err)) {
    const data: unknown = err.response?.data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const message = (data as { message: unknown }).message;
      if (typeof message === 'string') return message;
      if (Array.isArray(message)) return message.join('; ');
    }
  }
  return t('terminals.peripheralConfig.saveFailed', 'Could not save the hardware configuration.');
}
